/**
 * ArtifactRenderer - Orquestrador principal do sistema de Artifacts.
 *
 * Recebe o container (chat message text), identifica blocos artifact-* via parser,
 * delega para o renderer específico e monta o container (abotoado, sandbox) no DOM.
 */

import { ArtifactParser } from './ArtifactParser';
import { ArtifactSandbox } from './ArtifactSandbox';
import { ArtifactContainer } from './ArtifactContainer';
import { ReactRenderer } from './renderers/ReactRenderer';
import { HtmlRenderer } from './renderers/HtmlRenderer';
import { MermaidRenderer } from './renderers/MermaidRenderer';
import { JsonRenderer } from './renderers/JsonRenderer';
import { PythonRenderer } from './renderers/PythonRenderer';

export class ArtifactRenderer {
    /**
     * Inicia o processamento no DOM. Pode ser chamado após o markdown-it ou block processor formatar os nós.
     *
     * @param container Elemento que contém os nós renderizados (ex: `.chat-message-text`)
     */
    /**
     * Lê a cor da bolha AI configurada pelo usuário via CSS custom property.
     * Usada para injetar o background correto no documento interno do iframe seamless.
     */
    private static readBubbleAiColor(): string {
        const raw = getComputedStyle(document.documentElement)
            .getPropertyValue('--chat-bubble-ai')
            .trim();
        // Fallback para preto escuro se a variável não estiver definida
        return raw || '#2a2a2a';
    }

    static processArtifacts(container: HTMLElement): void {
        const blocks = ArtifactParser.parse(container);
        // Lido uma vez por chamada — todos os artifacts seamless do mesmo render compartilham a cor.
        const bubbleAiBg = ArtifactRenderer.readBubbleAiColor();

        blocks.forEach(block => {
            try {
                // 1. Determina a função geradora de HTML com base no tipo
                let renderToHtml: ((src: string) => string) | null = null;
                let minHeight = 200;
                let maxHeight: number | undefined = undefined; // Sem limite vertical por padrão

                switch (block.type) {
                    case 'react':
                        renderToHtml = ReactRenderer.renderToHtml;
                        break;
                    case 'html':
                        renderToHtml = HtmlRenderer.renderToHtml;
                        break;
                    case 'mermaid':
                        renderToHtml = MermaidRenderer.renderToHtml;
                        minHeight = 100;
                        maxHeight = 800;
                        break;
                    case 'json':
                        renderToHtml = JsonRenderer.renderToHtml;
                        minHeight = 150;
                        break;
                    case 'python':
                        renderToHtml = PythonRenderer.renderToHtml;
                        minHeight = 250;
                        break;
                    default:
                        console.warn(`[Artifacts] Tipo desconhecido ou não implementado: ${block.type}`);
                        return; // Deixa o bloco como código padrão
                }

                if (!renderToHtml) return;

                // 2. Cria o container UI (Abas PREVIEW | CODE)
                const uiContainer = new ArtifactContainer({
                    type: block.type,
                    source: block.source,
                    renderToHtml,
                    minHeight,
                    maxHeight,
                    seamless: block.seamless,
                });

                // 3. Gera o HTML inicial e monta o sandbox
                const initialHtml = renderToHtml(block.source);
                const sandbox = new ArtifactSandbox({
                    html: initialHtml,
                    minHeight,
                    maxHeight,
                    autoResize: true,
                    // Python (Pyodide) needs sessionStorage access for internal caching
                    allowSameOrigin: block.type === 'python',
                    seamless: block.seamless,
                    // Cor da bolha AI do usuário — injetada no html/body do iframe para
                    // que o fundo do documento corresponda exatamente ao fundo do chat.
                    bgColor: block.seamless ? bubbleAiBg : undefined,
                });

                // 4. Integra Sandbox no Container
                uiContainer.setSandbox(sandbox);

                // 5. Substitui o node original no DOM pelo novo Artifact
                if (block.element.parentNode) {
                    block.element.parentNode.replaceChild(uiContainer.getElement(), block.element);
                }

            } catch (err) {
                console.error(`[Artifacts] Falha geral ao montar o artifact ${block.type}:`, err);
                // Em caso de catch severo, o DOM permanece igual (<pre> default)
            }
        });
    }
}
